import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'


import { Loader2, Upload, ArrowLeft } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/ui/Avatar'
import { Button } from '../../../components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { useAuth } from '../../../contexts/AuthContext'
import { AuthService, UpdateProfileData } from '../../../services/AuthService'
import { Header } from '../../landing/components/Header'
import { useAppDispatch } from '../../../hooks/redux'
import { updateProfile } from './slice'
import { slugify } from '../../../utils/slugify'
import { Helmet } from 'react-helmet-async'


const EditProfilePage = () => {
  const navigate = useNavigate()
  const { profile, user } = useAuth()
  const authService = AuthService.getInstance()
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [fullName, setFullName] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [slugPreview, setSlugPreview] = useState('')
  const [isSlugAvailable, setIsSlugAvailable] = useState<boolean | null>(null)
  const [isCheckingSlug, setIsCheckingSlug] = useState(false)

  useEffect(() => {
    if (user) {
      setFullName(profile?.full_name || '')
      setAvatarUrl(profile?.avatar_url || '')
      setPreviewUrl(profile?.avatar_url || '')
    }
  }, [user])

  // Validate slug derived from full name without persisting it
  useEffect(() => {
    let active = true
    const run = async () => {
      if (!user) return
      const name = fullName.trim()
      if (!name) {
        setSlugPreview('')
        setIsSlugAvailable(null)
        return
      }
      const candidate = slugify(name)
      setSlugPreview(candidate)
      setIsCheckingSlug(true)
      try {
        const { available } = await authService.isUserSlugAvailable(candidate, user.id)
        if (!active) return
        setIsSlugAvailable(available)
      } catch (e) {
        if (!active) return
        setIsSlugAvailable(null)
      } finally {
        if (active) setIsCheckingSlug(false)
      }
    }
    const t = setTimeout(run, 400)
    return () => { active = false; clearTimeout(t) }
  }, [fullName, user])

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setError('Selecciona un archivo de imagen')
        return
      }
      
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo debe pesar menos de 5MB')
        return
      }

      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setError('')
      setSuccess('') // Clear success message when selecting new file
    }
  }

  const handleUploadAvatar = async () => {
    if (!selectedFile || !user) return

    setIsUploading(true)
    setError('')

    try {
      const { data: uploadedUrl, error: uploadError } = await authService.uploadAvatar(user.id, selectedFile)
      
      if (uploadError) {
        throw uploadError
      }

      if (uploadedUrl) {
        setAvatarUrl(uploadedUrl)
        setSuccess('¡Avatar subido con éxito!')
      }
    } catch (error: any) {
      setError(error.message || 'Error al subir el avatar')
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) return

    setIsLoading(true)
    setError('')
    setSuccess('')

    // Guard if derived slug is not available
    if (isSlugAvailable === false) {
      setError('Esta URL de perfil no está disponible. Ajusta tu nombre.')
      setIsLoading(false)
      return
    }

    try {
      // Si hay un archivo seleccionado, subirlo primero
      let finalAvatarUrl = avatarUrl
      if (selectedFile) {
        setIsUploading(true)
        const { data: uploadedUrl, error: uploadError } = await authService.uploadAvatar(user.id, selectedFile)
        
        if (uploadError) {
          throw uploadError
        }
        
        if (uploadedUrl) {
          finalAvatarUrl = uploadedUrl
        }
        setIsUploading(false)
      }

      const updateData: UpdateProfileData = {
        full_name: fullName.trim() || undefined,
        avatar_url: finalAvatarUrl || undefined
      }

      const {data: updateProfileData, error: updateError } = await authService.updateProfile(user.id, updateData)
      
      if (updateError) {
        throw updateError
      }

      if(updateProfileData) {
        // Actualizar el contexto de auth
        dispatch(updateProfile(updateProfileData ))
      }
      setSuccess('¡Perfil actualizado con éxito!')
      
      // Redirect after success
      setTimeout(() => {
        navigate('/dashboard')
      }, 1500)

    } catch (error: any) {
      setError(error.message || 'Failed to update profile')
      setIsUploading(false)
    } finally {
      setIsLoading(false)
    }
  }

  if (!user) {
    return (
      <div>
        
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Editar perfil | Punta Tech Meetup</title>
        <meta name="description" content="Actualizá tu nombre y avatar en la comunidad Punta Tech Meetup." />
      </Helmet>
      <Header onSignInClick={() => {}} />
      
      <div className="container mx-auto px-4 pt-24 pb-8">
        <div className="max-w-2xl mx-auto">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-6 p-0 h-auto font-normal text-purple-600 hover:text-purple-700"
          >
            ← Volver
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Editar perfil</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Avatar Section */}
                <div className="flex flex-col items-center space-y-4">
                  <Avatar className="w-24 h-24">
                    <AvatarImage src={previewUrl} alt="Profile" />
                    <AvatarFallback className="text-lg">
                      {fullName ? fullName.substring(0, 2).toUpperCase() : user.email?.substring(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <label htmlFor="avatar-upload" className="cursor-pointer">
                      <div className="flex items-center gap-2 px-4 py-2 border border-purple-300 text-purple-600 rounded-md hover:bg-purple-50 transition-colors">
                        <Upload className="w-4 h-4" />
                        Elegir foto
                      </div>
                      <Input
                        id="avatar-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                    </label>
                    
                    {selectedFile && (
                      <Button
                        type="button"
                        onClick={handleUploadAvatar}
                        disabled={isUploading}
                        variant="outline"
                        size="sm"
                      >
                        {isUploading ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : null}
                        Subir
                      </Button>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center">
                    Recomendado: Imagen cuadrada, mínimo 200x200px, máximo 5MB
                  </p>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Correo electrónico
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={user.email || ''}
                      disabled
                      className="bg-gray-50"
                    />
                    <p className="text-xs text-gray-500 mt-1">El correo no se puede cambiar</p>
                  </div>

                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre completo
                    </label>
                    <Input
                      id="fullName"
                      type="text"
                      value={fullName}
                      onChange={(e) => { setFullName(e.target.value); if (error) setError('') }}
                      placeholder="Ingresa tu nombre completo"
                      maxLength={100}
                    />
                    {fullName.trim() ? (
                      <div className="mt-1 text-xs">
                        <span className="text-gray-500">URL de perfil: </span>
                        <span className={isSlugAvailable === false ? 'text-red-600' : 'text-gray-800'}>/user/{slugPreview}</span>
                        {isCheckingSlug && <span className="ml-2 text-gray-500">Verificando...</span>}
                        {isSlugAvailable === true && <span className="ml-2 text-green-600">Disponible</span>}
                        {isSlugAvailable === false && <span className="ml-2 text-red-600">No disponible</span>}
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Error/Success Messages */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-sm text-green-600">{success}</p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || isSlugAvailable === false}
                    className="flex-1"
                    style={{ backgroundColor: 'black' }}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Guardando...
                      </>
                    ) : (
                      'Guardar'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default EditProfilePage